/**
 * AP-7 — EDS-Browser (FM-WIN-02) inklusive SDO-Client.
 *
 * Der Zustandsautomat ist die Portierung von OnSdoRawFrame() aus
 * EdsBrowserWindow.cpp: expedited und segmentiert, in beide Richtungen,
 * Abbruch in jeder Phase. Wie im Original gibt es **keinen** eigenen Timeout —
 * bleibt eine Antwort aus, steht die Anzeige still.
 */

import { createButton, createLabel } from '../ui/controls';
import { createSplitter } from '../ui/controls';
import { createFloatingPanel, type FloatingPanel } from './filters/FloatingPanel';
import { createDataTable } from './dataTable';
import './views.css';

const OBJECT_COLUMNS = ['Index', 'Name', 'Type', 'Access', 'Subs'] as const;
const SUB_COLUMNS = ['Sub', 'Name', 'DataType', 'Access', 'Default'] as const;

const START_HINT =
  'Select an object above, then Read/Write. Value: a number, or hex bytes like "DE AD BE EF" for DOMAIN/strings.';

export interface EdsSubEntry {
  subIndex: number;
  parameterName: string;
  dataType: string;
  accessType: string;
  defaultValue: string;
}

export interface EdsIndexEntry {
  index: number;
  parameterName: string;
  objectType: string;
  dataType: string;
  accessType: string;
  defaultValue: string;
  subs: EdsSubEntry[];
}

export interface EdsSnapshot {
  deviceName: string;
  entries: EdsIndexEntry[];
}

export interface EdsBrowserOptions {
  loadSnapshot: () => Promise<EdsSnapshot>;
  /** Sendet einen Rohframe; wirft, wenn nicht verbunden. */
  sendFrame: (frame: { id: number; dlc: number; data: Uint8Array }) => Promise<void>;
}

export interface EdsBrowserHandle {
  panel: FloatingPanel;
  refresh: () => Promise<void>;
  show: () => void;
  /** Jeden empfangenen Frame hier durchreichen; SDO-Antworten werden gefiltert. */
  onFrame: (id: number, data: Uint8Array, dlc: number) => void;
}

type SdoOp =
  | 'none'
  | 'awaiting-upload-initiate'
  | 'awaiting-upload-segment'
  | 'awaiting-download-initiate'
  | 'awaiting-download-segment-ack';

/** CanopenBasicTypeSize(): Fallback 4 fuer alles ausserhalb der Tabelle. */
function basicTypeSize(dataTypeHex: string): number {
  switch (Number.parseInt(dataTypeHex, 16)) {
    case 0x0002:
    case 0x0005:
      return 1;
    case 0x0003:
    case 0x0006:
      return 2;
    case 0x0010:
    case 0x0016:
      return 3;
    default:
      return 4;
  }
}

/**
 * ParseValueBytes(): ein Leerzeichen im Text bedeutet "rohe Hex-Bytefolge",
 * sonst eine einzelne Zahl (dezimal, oder hex mit 0x) in `fallbackByteLen` Bytes.
 */
export function parseValueBytes(text: string, fallbackByteLen: number): Uint8Array {
  if (text.includes(' ')) {
    const parts = text.split(' ').filter((part) => part !== '');
    return Uint8Array.from(parts, (part) => Number.parseInt(part, 16) & 0xff);
  }
  const value = (text.trim().toLowerCase().startsWith('0x')
    ? Number.parseInt(text.trim().slice(2), 16)
    : Number.parseInt(text, 10)) >>> 0;
  if (Number.isNaN(value)) return new Uint8Array(0);
  const length = Math.min(4, Math.max(1, fallbackByteLen));
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) bytes[i] = (value >>> (8 * i)) & 0xff;
  return bytes;
}

function hex(bytes: Uint8Array | readonly number[]): string {
  return Array.from(bytes, (byte) => byte.toString(16).toUpperCase().padStart(2, '0')).join(' ');
}

function sdoRequest(nodeId: number, data: Uint8Array): { id: number; dlc: number; data: Uint8Array } {
  return { id: 0x600 + nodeId, dlc: 8, data };
}

export function createEdsBrowser(options: EdsBrowserOptions): EdsBrowserHandle {
  const panel = createFloatingPanel('EDS Browser');

  const view = document.createElement('div');
  view.className = 'cm-view';

  const searchRow = document.createElement('div');
  searchRow.className = 'cm-view__row';
  const search = document.createElement('input');
  search.type = 'text';
  search.className = 'cm-input';
  searchRow.append(createLabel('Search:'), search);

  const split = document.createElement('div');
  split.className = 'cm-view__split';

  let entries: EdsIndexEntry[] = [];
  let filteredIndices: number[] = [];

  const subTable = createDataTable({ columns: SUB_COLUMNS, emptyText: '', onSelect: () => undefined });
  const objectTable = createDataTable({
    columns: OBJECT_COLUMNS,
    emptyText: 'Keine EDS geladen.',
    onSelect: (row) => showSubs(row),
  });

  const splitter = createSplitter(split, {
    orientation: 'vertical',
    minRatio: 0.15,
    onRatioChanged: (ratio) => split.style.setProperty('--split-ratio', String(ratio)),
  });
  split.append(objectTable.element, splitter, subTable.element);

  // --- SDO-Bereich ---------------------------------------------------------
  const sdoRow = document.createElement('div');
  sdoRow.className = 'cm-view__row';
  const nodeIdInput = document.createElement('input');
  nodeIdInput.type = 'text';
  nodeIdInput.className = 'cm-input';
  nodeIdInput.value = '1';
  nodeIdInput.size = 4;
  const valueInput = document.createElement('input');
  valueInput.type = 'text';
  valueInput.className = 'cm-input';
  valueInput.value = '0';

  const result = document.createElement('div');
  result.className = 'cm-view__result';
  result.textContent = START_HINT;

  sdoRow.append(
    createLabel('Node ID:'),
    nodeIdInput,
    createButton('SDO Read', { onClick: () => void onRead() }),
    createLabel('Value:'),
    valueInput,
    createButton('SDO Write', { onClick: () => void onWrite() }),
  );

  view.append(searchRow, split, sdoRow, result);
  panel.body.append(view);

  // --- SDO-Zustand ---------------------------------------------------------
  let pendingOp: SdoOp = 'none';
  let pendingNodeId = 0;
  let pendingIndex = 0;
  let pendingSubIndex = 0;
  let toggleBit = false;
  let segmentedRead: number[] = [];
  let segmentedWrite: number[] = [];

  function showSubs(row: number): void {
    const entryIndex = filteredIndices[row];
    const entry = entryIndex === undefined ? undefined : entries[entryIndex];
    if (entry === undefined) {
      subTable.clear();
      return;
    }
    subTable.setRows(
      entry.subs.map((sub) => [
        String(sub.subIndex),
        sub.parameterName,
        sub.dataType,
        sub.accessType,
        sub.defaultValue,
      ]),
    );
  }

  function populate(): void {
    const query = search.value.toLowerCase();
    filteredIndices = [];
    entries.forEach((entry, index) => {
      if (query !== '' && !entry.parameterName.toLowerCase().includes(query)) return;
      filteredIndices.push(index);
    });
    objectTable.setRows(
      filteredIndices.map((index) => {
        const entry = entries[index] as EdsIndexEntry;
        return [
          entry.index.toString(16).toUpperCase().padStart(4, '0'),
          entry.parameterName,
          entry.objectType,
          entry.accessType,
          String(entry.subs.length),
        ];
      }),
    );
    subTable.clear();
  }

  search.addEventListener('input', () => populate());

  /** GetSelectedIndexSub(): ARRAY/RECORD ohne gewaehlten Sub ist mehrdeutig. */
  function selectedTarget(): { index: number; subIndex: number; byteLen: number } | null {
    const entryIndex = filteredIndices[objectTable.selectedRow];
    const entry = entryIndex === undefined ? undefined : entries[entryIndex];
    if (entry === undefined) return null;
    if (entry.subs.length === 0) {
      return { index: entry.index, subIndex: 0, byteLen: basicTypeSize(entry.dataType) };
    }
    const sub = entry.subs[subTable.selectedRow];
    if (sub === undefined) return null;
    return { index: entry.index, subIndex: sub.subIndex, byteLen: basicTypeSize(sub.dataType) };
  }

  function nodeId(): number {
    return Number.parseInt(nodeIdInput.value, 10) & 0xff;
  }

  async function send(data: Uint8Array, node: number): Promise<boolean> {
    try {
      await options.sendFrame(sdoRequest(node, data));
      return true;
    } catch (error) {
      result.textContent = `Send failed: ${error instanceof Error ? error.message : String(error)}`;
      return false;
    }
  }

  async function onRead(): Promise<void> {
    const target = selectedTarget();
    if (target === null) {
      result.textContent = 'Select an object (and sub-index, if any) above first.';
      return;
    }
    const node = nodeId();
    const data = new Uint8Array(8);
    data[0] = 0x40; // Initiate Upload, unabhaengig von expedited/segmentiert
    data[1] = target.index & 0xff;
    data[2] = (target.index >> 8) & 0xff;
    data[3] = target.subIndex;
    if (!(await send(data, node))) return;

    pendingOp = 'awaiting-upload-initiate';
    pendingNodeId = node;
    pendingIndex = target.index;
    pendingSubIndex = target.subIndex;
    segmentedRead = [];
    result.textContent =
      `Read request sent for Node ${node}, 0x${target.index.toString(16).toUpperCase().padStart(4, '0')}` +
      `:${target.subIndex} -- waiting for response...`;
  }

  async function onWrite(): Promise<void> {
    const target = selectedTarget();
    if (target === null) {
      result.textContent = 'Select an object (and sub-index, if any) above first.';
      return;
    }
    const bytes = parseValueBytes(valueInput.value, target.byteLen);
    if (bytes.length === 0) {
      result.textContent = 'Enter a value to write first.';
      return;
    }
    const node = nodeId();
    const data = new Uint8Array(8);
    data[1] = target.index & 0xff;
    data[2] = (target.index >> 8) & 0xff;
    data[3] = target.subIndex;

    if (bytes.length <= 4) {
      // Expedited: der Wert passt in die Initiate-Anforderung selbst.
      const n = bytes.length;
      data[0] = 0x20 | ((4 - n) << 2) | 0x02 | 0x01;
      for (let i = 0; i < n; i += 1) data[4 + i] = bytes[i] ?? 0;
      if (!(await send(data, node))) return;
      pendingOp = 'awaiting-upload-initiate'; // dieselbe Wartephase quittiert den Write-Ack
    } else {
      data[0] = 0x21; // segmentiert, Groesse angegeben
      for (let i = 0; i < 4; i += 1) data[4 + i] = (bytes.length >>> (8 * i)) & 0xff;
      if (!(await send(data, node))) return;
      pendingOp = 'awaiting-download-initiate';
      segmentedWrite = Array.from(bytes);
      toggleBit = false;
    }
    pendingNodeId = node;
    pendingIndex = target.index;
    pendingSubIndex = target.subIndex;
    const plural = bytes.length === 1 ? '' : 's';
    result.textContent =
      `Write request sent for Node ${node}, 0x${target.index.toString(16).toUpperCase().padStart(4, '0')}` +
      `:${target.subIndex} (${bytes.length} byte${plural}) -- waiting for response...`;
  }

  function targetText(): string {
    return `Node ${pendingNodeId}, 0x${pendingIndex.toString(16).toUpperCase().padStart(4, '0')}:${pendingSubIndex}`;
  }

  function showUploadResult(bytes: number[]): void {
    const dump = bytes.length === 0 ? '-' : `${hex(bytes)} `;
    if (bytes.length <= 4) {
      let value = 0;
      for (let i = 0; i < bytes.length; i += 1) value |= (bytes[i] ?? 0) << (8 * i);
      result.textContent = `${targetText()} = ${value >>> 0} (${dump})`;
    } else {
      result.textContent = `${targetText()} = ${bytes.length} bytes: ${dump}`;
    }
  }

  async function sendNextDownloadSegment(): Promise<void> {
    const n = Math.min(7, segmentedWrite.length);
    const isLast = n === segmentedWrite.length;
    const data = new Uint8Array(8);
    data[0] = (toggleBit ? 0x10 : 0x00) | ((7 - n) << 1) | (isLast ? 0x01 : 0x00);
    for (let i = 0; i < n; i += 1) data[1 + i] = segmentedWrite[i] ?? 0;
    segmentedWrite = segmentedWrite.slice(n);
    await send(data, pendingNodeId);
  }

  async function requestUploadSegment(): Promise<void> {
    const data = new Uint8Array(8);
    data[0] = 0x60 | (toggleBit ? 0x10 : 0x00);
    await send(data, pendingNodeId);
  }

  function onSdoResponse(node: number, data: Uint8Array, dlc: number): void {
    if (pendingOp === 'none' || node !== pendingNodeId || dlc < 1) return;
    const cs = data[0] ?? 0;

    if ((cs & 0x80) !== 0) {
      // Abbruch — kann in jeder Phase und Richtung eintreffen.
      let abortCode = 0;
      if (dlc >= 8) {
        abortCode =
          ((data[4] ?? 0) | ((data[5] ?? 0) << 8) | ((data[6] ?? 0) << 16) | ((data[7] ?? 0) << 24)) >>> 0;
      }
      result.textContent = `${targetText()} -> Abort (code 0x${abortCode.toString(16).toUpperCase().padStart(8, '0')})`;
      pendingOp = 'none';
      return;
    }

    switch (pendingOp) {
      case 'awaiting-upload-initiate': {
        if (dlc < 4) return;
        const respIndex = (data[1] ?? 0) | ((data[2] ?? 0) << 8);
        if (respIndex !== pendingIndex || (data[3] ?? 0) !== pendingSubIndex) return;

        if ((cs & 0xe0) === 0x60) {
          result.textContent = `${targetText()} -> Write acknowledged.`;
          pendingOp = 'none';
          return;
        }
        if ((cs & 0xe0) !== 0x40) return; // keine Initiate-Upload-Antwort

        if ((cs & 0x02) !== 0) {
          const n = (cs & 0x01) !== 0 ? 4 - ((cs >> 2) & 0x3) : 4;
          showUploadResult(Array.from(data.subarray(4, 4 + n)));
          pendingOp = 'none';
        } else {
          toggleBit = false;
          pendingOp = 'awaiting-upload-segment';
          void requestUploadSegment();
        }
        return;
      }
      case 'awaiting-upload-segment': {
        if (((cs & 0x10) !== 0) !== toggleBit) return; // veraltet/doppelt
        const n = 7 - ((cs >> 1) & 0x7);
        for (let i = 0; i < n && i < 7; i += 1) segmentedRead.push(data[1 + i] ?? 0);
        if ((cs & 0x01) !== 0) {
          showUploadResult(segmentedRead);
          pendingOp = 'none';
        } else {
          toggleBit = !toggleBit;
          void requestUploadSegment();
        }
        return;
      }
      case 'awaiting-download-initiate': {
        if (dlc < 4) return;
        const respIndex = (data[1] ?? 0) | ((data[2] ?? 0) << 8);
        if (respIndex !== pendingIndex || (data[3] ?? 0) !== pendingSubIndex) return;
        toggleBit = false;
        pendingOp = 'awaiting-download-segment-ack';
        void sendNextDownloadSegment();
        return;
      }
      case 'awaiting-download-segment-ack': {
        if (((cs & 0x10) !== 0) !== toggleBit) return;
        if (segmentedWrite.length === 0) {
          result.textContent = `${targetText()} -> Write complete.`;
          pendingOp = 'none';
        } else {
          toggleBit = !toggleBit;
          void sendNextDownloadSegment();
        }
        return;
      }
      default:
        return;
    }
  }

  async function refresh(): Promise<void> {
    try {
      const snapshot = await options.loadSnapshot();
      entries = snapshot.entries ?? [];
    } catch {
      entries = [];
    }
    populate();
  }

  return {
    panel,
    refresh,
    show() {
      panel.show();
      void refresh();
    },
    onFrame(id, data, dlc) {
      // SDO-Server-Antworten liegen auf 0x580 + Node-ID.
      if (id < 0x581 || id > 0x5ff) return;
      onSdoResponse(id - 0x580, data, dlc);
    },
  };
}
